import {
  BILLING_PERIODS, CREDIT_DEFAULTS, CUSTOM_ADJUSTMENT_MAX_HOURS, CUSTOM_ADJUSTMENTS_PER_YEAR, PLANS, TRIAL_REMINDER_DAYS, choiceEndsTrial, discountOn, formatMoney, founderActive, inTrial, inUpgradeWeek, nextPeriodStart,
  periodCharge, trialDaysLeft, unitPrices, upgradeDaysLeft, upgradeWeekEnded, withVat, type BillingPeriod, type Plan,
} from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { ProfiWeekEnded, TrialSummary, trialCard, useChoosePlan, useDate } from "../components/TrialGuide";
import { useApp } from "../context";
import type { MyCustomRequests, MySubscription } from "../data";

/** Short notes of the subscription (trial, founder offer) for the Settings page and the reminder. */
export function useSubscription() {
  const { store, workshop } = useApp();
  const [sub, setSub] = useState<MySubscription | null>(null);
  const load = useCallback(async () => {
    try {
      setSub(await store.mySubscription(workshop.id));
    } catch {
      setSub(null);
    }
  }, [store, workshop.id]);
  useEffect(() => void load(), [load]);
  return { sub, reload: load };
}

/** The trial and founder notes (Settings, the Subscription page). */
export function SubscriptionNotes({ sub }: { sub: MySubscription }) {
  const { t } = useTranslation();
  const date = useDate();
  const days = trialDaysLeft(sub, sub.today);
  return (
    <>
      {sub.readOnly ? (
        <p className="warn" role="status"><strong>{t("subscription.readOnlyBanner")}</strong></p>
      ) : days > 0 ? (
        <p className={days <= TRIAL_REMINDER_DAYS ? "warn" : "sub-note"} role="status">
          <strong>{t("subscription.trialNote", { days, plan: t(`plan.${sub.effectivePlan}.label`) })}</strong>{" "}
          {t("subscription.trialUntil", { date: date(sub.trialUntil) })}
          {inUpgradeWeek(sub, sub.today) && <> {t("subscription.trialWeekNote", { days: upgradeDaysLeft(sub, sub.today), plan: t(`plan.${sub.trialUpgradePlan ?? "medium"}.label`) })}</>}
          {upgradeWeekEnded(sub, sub.today) && <> {t("subscription.trialWeekEnded", { plan: t(`plan.${sub.trialUpgradePlan ?? "medium"}.label`) })}</>}
        </p>
      ) : (
        <p className="sub-note">{sub.status === "free" ? t("subscription.free") : t("subscription.currentPlan", { plan: t(`plan.${sub.effectivePlan}.label`) })}</p>
      )}
      {sub.founder && sub.founderUntil && sub.founderUntil >= sub.today && (
        <p className="sub-note founder">
          <strong>{t("subscription.founderNote", { percent: sub.pricing.founder.percent, date: date(sub.founderUntil) })}</strong>
          {sub.priceLockUntil && <> {t("subscription.founderLock", { date: date(sub.priceLockUntil) })}</>}
        </p>
      )}
    </>
  );
}

/**
 * On every page: after the trial without a choice the read only banner (the owner gets the plan choice
 * button, the others are told to ask the owner); in the last days of the trial the orange reminder (owner);
 * before that the Profi week's summary (its last two days) or the banner after it (owner, until a choice).
 */
export function TrialReminder({ sub, isOwner, onChoose }: { sub: MySubscription; isOwner: boolean; onChoose: () => void }) {
  const { t } = useTranslation();
  if (sub.readOnly) {
    return (
      <div className="billing-notice trial-reminder read-only" role="alert">
        <p><strong>{t(isOwner ? "subscription.readOnlyBanner" : "subscription.readOnlyMember")}</strong></p>
        {isOwner && <button type="button" className="primary" onClick={onChoose}>{t("subscription.choosePlan")}</button>}
      </div>
    );
  }
  if (!isOwner) return null;
  const days = trialDaysLeft(sub, sub.today);
  if (days > 0 && days <= TRIAL_REMINDER_DAYS) {
    return (
      <div className="billing-notice trial-reminder" role="status">
        <p>
          <strong>{t("subscription.trialEndingTitle", { days })}</strong> {t("subscription.trialEndingText")}
        </p>
        <p className="small">{t("trial.noNeed")}</p>
        <button type="button" className="primary" onClick={onChoose}>{t("subscription.choosePlan")}</button>
      </div>
    );
  }
  const card = trialCard(sub);
  if (card === "summary") return <TrialSummary sub={sub} />;
  if (card === "weekEnded") return <ProfiWeekEnded sub={sub} />;
  return null;
}

/**
 * The owner's Subscription page (Settings > Előfizetés): the three plans with their prices, the trial and
 * the founder offer, monthly or yearly payment, extra users and the resulting amount. Phone first.
 */
export function Subscription() {
  const { t, i18n } = useTranslation();
  const { store, workshop, role, reloadWorkshop, back } = useApp();
  const { sub, reload } = useSubscription();
  const { choose, error: chooseError } = useChoosePlan();
  const [extra, setExtra] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  // included Okos lekérdezés credits per plan (the plan cards), from the credit rules
  const [planCredits, setPlanCredits] = useState<Record<Plan, number>>(CREDIT_DEFAULTS.allowance);
  useEffect(() => {
    let live = true;
    store.aiCredits(workshop.id).then((c) => live && setPlanCredits(c.planCredits), () => undefined);
    return () => { live = false; };
  }, [store, workshop.id]);
  const date = useDate();
  const money = (n: number) => formatMoney(n, "HUF", i18n.language);
  useEffect(() => setExtra(sub ? sub.extraSeats : null), [sub]);

  if (!sub) return <section className="narrow subscription"><h1>{t("subscription.title")}</h1></section>;
  const isOwner = role === "owner";
  const today = sub.today;
  const trial = inTrial(sub, today);
  // the plan is the owner's choice during the trial and while read only after it
  const canChoose = trial || sub.readOnly;
  const p = sub.pricing;
  const vat = p.vatRate;
  // the plan after the trial (or the current one)
  const chosen: Plan = sub.plan;
  // the card marked as chosen: none until the owner chose (in the trial and while read only)
  const picked: Plan | null = canChoose && !sub.planAfterTrial ? null : chosen;
  const seats = extra ?? sub.extraSeats;

  const change = async (patch: Parameters<typeof store.setSubscription>[1]) => {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await store.setSubscription(workshop.id, patch);
      await reload();
      await reloadWorkshop();
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // what the next period costs with the choices on the page
  // (before a choice: as if the shown plan were chosen now; read only: paid from today)
  const state = { ...sub, extraSeats: seats, planAfterTrial: sub.planAfterTrial ?? chosen, ...(sub.readOnly ? { billingAnchor: today } : {}) };
  const start = sub.status === "free" ? null : nextPeriodStart(state, today);
  const charge = start ? periodCharge(state, p, start) : null;
  const seatPrice = unitPrices(state, p, start ?? today).seatNet;
  // the seats of the plan that is paid for (the chosen one; Premium only while trying)
  const planLimit = sub.seats[chosen];
  const over = sub.seatsUsed > sub.seatLimit;

  return (
    <section className="narrow subscription">
      <div className="page-head">
        <h1>{t("subscription.title")} <Help id="subscription.page" /></h1>
        <button type="button" onClick={() => back("settings")}>{t("common.back")}</button>
      </div>
      <p className="muted">{t("subscription.intro", { vat })}</p>
      <SubscriptionNotes sub={sub} />
      {trial && <p className="muted">{sub.planAfterTrial ? t("subscription.trialAfter", { plan: t(`plan.${chosen}.label`) }) : t("subscription.trialNoChoice")}</p>}
      {(trial || sub.readOnly) && <p className="muted">{t("subscription.trialChoiceNote")} {t("trial.noNeed")}</p>}
      {!isOwner && <p className="muted">{t("subscription.ownerOnly")}</p>}
      <ErrorText error={error ?? chooseError} />
      {saved && <p className="ok" role="status">{t("common.saved")}</p>}

      <fieldset className="choice-cards plan-cards" disabled={!isOwner || !canChoose || busy}>
        <legend>{t("subscription.plansTitle")}</legend>
        {PLANS.map((plan) => {
          const net = sub.period === "yearly" ? p.plans[plan] * (12 - p.yearlyFreeMonths) : p.plans[plan];
          const g = withVat(net, vat);
          const founderNet = Math.round((p.plans[plan] * (100 - discountOn(sub, p, sub.billingAnchor ?? today))) / 100);
          const showFounder = founderActive(sub, sub.billingAnchor ?? today) && founderNet < p.plans[plan];
          return (
            <label key={plan} className={`choice-card plan-card${picked === plan ? " selected" : ""}`}>
              <input
                type="radio" name="plan" checked={picked === plan} aria-label={t(`plan.${plan}.label`)}
                onChange={() => void choose(plan, sub).then(async (ok) => {
                  if (!ok) return;
                  await reload();
                  setSaved(true);
                })}
              />
              <span>
                <strong className="plan-name">{t(`plan.${plan}.label`)}</strong>
                {picked === plan && <span className="badge ok">{t(trial ? "subscription.badgeAfter" : "subscription.badgeCurrent")}</span>}
                {(trial || sub.readOnly) && plan === sub.effectivePlan && <span className="badge">{t("subscription.badgeNow")}</span>}
                {trial && !sub.planAfterTrial && <><br /><span className="muted small">{t(choiceEndsTrial(plan) ? "subscription.choiceStartsNow" : "subscription.choiceAfterTrial", { date: date(sub.billingAnchor) })}</span></>}
                <br />
                <span className="plan-price">
                  {t(sub.period === "yearly" ? "subscription.planPriceYear" : "subscription.planPrice", { net: money(net) })}
                </span>
                <br />
                <span className="muted">{t(sub.period === "yearly" ? "subscription.planGrossYear" : "subscription.planGross", { gross: money(g.gross) })}</span>
                {showFounder && (
                  <>
                    <br />
                    <span className="paid-note">{t("subscription.planFounder", { net: money(founderNet), months: p.founder.months })}</span>
                  </>
                )}
                <br />
                {t("subscription.planSeats", { count: sub.seats[plan] })} · {t("subscription.planAi", { count: planCredits[plan] })}
                <br />
                <span className="muted small">{planCustomText(t, plan)}</span>
              </span>
            </label>
          );
        })}
      </fieldset>
      {isOwner && !canChoose && <p className="muted">{t("subscription.planLocked")}</p>}

      <fieldset className="choice-cards" disabled={!isOwner || busy}>
        <legend>{t("subscription.periodTitle")}</legend>
        {BILLING_PERIODS.map((period: BillingPeriod) => (
          <label key={period} className={`choice-card${sub.period === period ? " selected" : ""}`}>
            <input type="radio" name="period" checked={sub.period === period} onChange={() => void change({ period })} aria-label={t(`subscription.period.${period}`, { free: p.yearlyFreeMonths })} />
            <span>
              <strong>{t(`subscription.period.${period}`, { free: p.yearlyFreeMonths })}</strong>
              <br />
              <span className="muted">
                {period === "yearly"
                  ? t("subscription.periodYearlyText", { paid: 12 - p.yearlyFreeMonths, net: money(p.plans[chosen] * (12 - p.yearlyFreeMonths)) })
                  : t("subscription.periodMonthlyText", { net: money(p.plans[chosen]) })}
              </span>
            </span>
          </label>
        ))}
      </fieldset>

      <div className="sub-extra">
        <h2>{t("subscription.extraTitle")}</h2>
        <p className="muted">{t("subscription.extraText", { seats: planLimit, price: money(seatPrice) })}</p>
        <p className={over ? "warn" : "muted"}>{t(over ? "subscription.seatsOver" : "subscription.seatsUsed", { used: sub.seatsUsed, limit: sub.seatLimit })}</p>
        <div className="stepper" role="group" aria-label={t("subscription.extraSeats")}>
          <button type="button" disabled={!isOwner || seats <= 0 || busy} onClick={() => setExtra(seats - 1)} aria-label={t("subscription.extraLess")}>−</button>
          <output aria-live="polite" aria-label={t("subscription.extraSeats")}>{seats}</output>
          <button type="button" disabled={!isOwner || seats >= 1000 || busy} onClick={() => setExtra(seats + 1)} aria-label={t("subscription.extraMore")}>+</button>
          {isOwner && (
            <button type="button" className="primary" disabled={seats === sub.extraSeats || busy} onClick={() => void change({ extraSeats: seats })}>
              {t("subscription.extraSave")}
            </button>
          )}
        </div>
        <p>{t("subscription.extraCost", { count: seats, price: money(seatPrice), total: money(seats * seatPrice) })}</p>
      </div>

      {isOwner && <CustomAdjustments />}

      <div className="sub-amount" aria-label={t("subscription.amountTitle")} role="region">
        <h2>{t("subscription.amountTitle")}</h2>
        {sub.status === "free" || !charge || !start ? (
          <p>{t("subscription.free")}</p>
        ) : (
          <>
            <p>{trial ? t("subscription.amountTrial", { date: date(start) }) : t("subscription.amountNext", { date: date(start) })}</p>
            <table className="list sub-lines">
              <tbody>
                {charge.lines.map((l) => (
                  <tr key={l.kind}>
                    <td>
                      {l.kind === "plan"
                        ? t("subscription.line.plan", { plan: t(`plan.${chosen}.label`), months: l.quantity })
                        : t("subscription.line.extraSeats", { count: seats, months: sub.period === "yearly" ? 12 : 1 })}
                      {l.listNet > l.net && <small className="muted"> {t("subscription.instead", { list: money(l.listNet) })}</small>}
                    </td>
                    <td className="num">{money(l.net)}</td>
                  </tr>
                ))}
                <tr><td>{t("subscription.net")}</td><td className="num">{money(charge.net)}</td></tr>
                <tr><td>{t("subscription.vat", { rate: vat })}</td><td className="num">{money(charge.vat)}</td></tr>
                <tr className="total"><td><strong>{t("subscription.gross")}</strong></td><td className="num"><strong>{money(charge.gross)}</strong></td></tr>
              </tbody>
            </table>
            {sub.period === "yearly" && <p className="muted">{t("subscription.perMonth", { gross: money(Math.round(charge.gross / 12)) })}</p>}
          </>
        )}
        <p className="muted small">{t("subscription.usageNote", { sms: money(sub.smsNet), ai: money(sub.aiCreditGross) })}</p>
        <p className="muted small">{t("subscription.invoiceNote")}</p>
        <p className="muted small">{t("subscription.cancelNote")}</p>
      </div>
    </section>
  );
}

/** The custom adjustment line of a plan card: none (individual offer), included a year, or included with priority. */
export function planCustomText(t: (key: string, o?: Record<string, unknown>) => string, plan: Plan): string {
  const count = CUSTOM_ADJUSTMENTS_PER_YEAR[plan];
  if (count === 0) return t("subscription.planCustom.none");
  return t(plan === "premium" ? "subscription.planCustom.priority" : "subscription.planCustom.included", { count, hours: CUSTOM_ADJUSTMENT_MAX_HOURS });
}

/** The owner's counter of this year's custom adjustments (the operator records them). */
function CustomAdjustments() {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [data, setData] = useState<MyCustomRequests | null>(null);
  useEffect(() => {
    let live = true;
    store.myCustomRequests(workshop.id).then((d) => live && setData(d), () => live && setData(null));
    return () => {
      live = false;
    };
  }, [store, workshop.id]);
  const hours = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(n);
  return (
    <div className="sub-custom" role="region" aria-label={t("subscription.customTitle")}>
      <h2>{t("subscription.customTitle")}</h2>
      {data && data.included > 0 && <p><strong>{t("subscription.customUsed", { used: data.used, included: data.included })}</strong></p>}
      {data && data.included === 0 && (
        <p className="muted">
          {t("subscription.customNone", {
            medium: CUSTOM_ADJUSTMENTS_PER_YEAR.medium, premium: CUSTOM_ADJUSTMENTS_PER_YEAR.premium,
            mediumName: t("plan.medium.label"), premiumName: t("plan.premium.label"),
          })}
        </p>
      )}
      {data && data.requests.length > 0 && (
        <ul className="sub-custom-list">
          {data.requests.map((r) => (
            <li key={r.id}>
              {r.title} <span className="muted small">· {t("subscription.customHours", { hours: hours(r.hours) })} · {t(`customRequest.status.${r.status}`)}{!r.included && <> · {t("subscription.customOffer")}</>}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">{t("subscription.customText")}</p>
      <p className="muted small">{t("subscription.customAsk")}</p>
    </div>
  );
}
